/**
 * Deterministic edge refinement of the AI contour (README 3.7.1, "Ajuste fino por borda").
 *
 * The vision model returns a plausible outline that can sit several percent of
 * the image off the real head edge. Each contour point is moved along the ray
 * from the contour centroid to the nearest clear head -> background step of a
 * colour feature ("background-ness", projection of the RGB onto the inside ->
 * outside colour axis). Two passes with a shrinking window, robust
 * regularization of the shifts and an arc-length resample. Pure functions over
 * an RGBA buffer: no DOM, testable with synthetic pixels.
 */

import { calculateContourCentroid } from "./cranialGeometry";

export const DEFAULT_REFINEMENT_OPTIONS = {
  // First-pass window R = clamp(round(radiusFactor * max(width, height)), minRadiusPx, maxRadiusPx);
  // each later pass multiplies R by passRadiusFactor.
  radiusFactor: 0.08,
  minRadiusPx: 12,
  maxRadiusPx: 120,
  passes: 2,
  passRadiusFactor: 0.5,
  // Colour statistics: inside samples at 40-70 % and outside samples at 115-140 % of each point's radius.
  insideRing: [0.4, 0.7],
  outsideRing: [1.15, 1.4],
  ringSamples: 6,
  // Below this RGB distance between inside and outside the feature falls back to luminance.
  minColorContrast: 8,
  // A candidate needs gradient >= max(absoluteMinGradient, relativeGradientFactor * median |gradient| of the profile)
  absoluteMinGradient: 4,
  relativeGradientFactor: 2.5,
  // ... and mean(feature over stepWindowPx beyond) - mean(stepWindowPx before) >= stepContrastFraction * contrast.
  stepWindowPx: 6,
  stepContrastFraction: 0.6,
  // Score = gradient * (1 - distanceWeight * |d| / R): nearby edges win.
  distanceWeight: 0.35,
  // Regularization: circular median window and outlier tolerance (fraction of R).
  medianWindow: 5,
  outlierFactor: 0.25,
  // A pass moves nothing when fewer than this fraction of points found an edge.
  minKnownFraction: 0.2,
};

// 5-tap binomial kernel, a close approximation of a Gaussian with sigma = 1.
const BLUR_KERNEL = [1 / 16, 4 / 16, 6 / 16, 4 / 16, 1 / 16];
// Final displacements below this are reported as "not moved".
const MOVED_EPSILON_PX = 0.1;
// The inward part of the window never goes past this fraction of the point's radius.
const MAX_INWARD_FRACTION = 0.8;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const clamp01 = (value) => clamp(value, 0, 1);
const isPoint = (point) => Boolean(point) && Number.isFinite(point.x) && Number.isFinite(point.y);
const luminance = (rgb) => 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];

const median = (values) => {
  if (values.length === 0) return 0;
  const sorted = Array.from(values).sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
};

const normalize = (vector) => {
  const length = Math.hypot(vector.x, vector.y);
  return length > 1e-9 ? { x: vector.x / length, y: vector.y / length } : null;
};

const blur1D = (values) =>
  values.map((_, i) => {
    let sum = 0;
    for (let k = -2; k <= 2; k++) sum += BLUR_KERNEL[k + 2] * values[clamp(i + k, 0, values.length - 1)];
    return sum;
  });

const blurPlane = (source, width, height, horizontal) => {
  const target = new Float32Array(source.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let sum = 0;
      for (let k = -2; k <= 2; k++) {
        const sx = horizontal ? clamp(x + k, 0, width - 1) : x;
        const sy = horizontal ? y : clamp(y + k, 0, height - 1);
        sum += BLUR_KERNEL[k + 2] * source[sy * width + sx];
      }
      target[y * width + x] = sum;
    }
  }
  return target;
};

/** Three separably blurred colour planes (5x5 binomial). */
const toBlurredRgb = (pixels, width, height) =>
  [0, 1, 2].map((channel) => {
    const plane = new Float32Array(width * height);
    for (let i = 0, p = channel; i < plane.length; i++, p += 4) plane[i] = pixels[p];
    return blurPlane(blurPlane(plane, width, height, true), width, height, false);
  });

/** Bilinear RGB sample at a continuous pixel position (pixel i has its centre at i + 0.5). */
const sampleRgb = (image, x, y) => {
  const { planes, width, height } = image;
  const fx = clamp(x - 0.5, 0, width - 1);
  const fy = clamp(y - 0.5, 0, height - 1);
  const x0 = Math.floor(fx);
  const y0 = Math.floor(fy);
  const x1 = Math.min(x0 + 1, width - 1);
  const y1 = Math.min(y0 + 1, height - 1);
  const tx = fx - x0;
  const ty = fy - y0;
  return planes.map((data) => {
    const top = data[y0 * width + x0] * (1 - tx) + data[y0 * width + x1] * tx;
    const bottom = data[y1 * width + x0] * (1 - tx) + data[y1 * width + x1] * tx;
    return top * (1 - ty) + bottom * ty;
  });
};

/**
 * Inside / outside colour of the current contour (per-channel median of samples
 * along centroid rays) and the scalar "background-ness" feature derived from it.
 */
const buildFeature = (image, points, centroid, settings) => {
  const inside = [];
  const outside = [];
  const rays = points.flatMap((point, i) => {
    const next = points[(i + 1) % points.length];
    return [point, { x: (point.x + next.x) / 2, y: (point.y + next.y) / 2 }];
  });
  const collect = (target, [from, to]) => {
    rays.forEach((ray) => {
      for (let s = 0; s < settings.ringSamples; s++) {
        const t = from + ((to - from) * s) / (settings.ringSamples - 1);
        const x = centroid.x + (ray.x - centroid.x) * t;
        const y = centroid.y + (ray.y - centroid.y) * t;
        if (x >= 0 && y >= 0 && x <= image.width && y <= image.height) target.push(sampleRgb(image, x, y));
      }
    });
  };
  collect(inside, settings.insideRing);
  collect(outside, settings.outsideRing);
  if (inside.length === 0 || outside.length === 0) return null;

  const channelMedian = (samples) => [0, 1, 2].map((c) => median(samples.map((rgb) => rgb[c])));
  const inRgb = channelMedian(inside);
  const outRgb = channelMedian(outside);
  const diff = outRgb.map((value, c) => value - inRgb[c]);
  const contrast = Math.hypot(...diff);

  if (contrast >= settings.minColorContrast) {
    const axis = diff.map((value) => value / contrast);
    const at = (x, y) => {
      const rgb = sampleRgb(image, x, y);
      return (rgb[0] - inRgb[0]) * axis[0] + (rgb[1] - inRgb[1]) * axis[1] + (rgb[2] - inRgb[2]) * axis[2];
    };
    return { at, contrast, mode: "color" };
  }
  const insideLuminance = luminance(inRgb);
  const sign = luminance(outRgb) >= insideLuminance ? 1 : -1;
  return { at: (x, y) => sign * (luminance(sampleRgb(image, x, y)) - insideLuminance), contrast, mode: "luminance" };
};

/**
 * Signed offset (px) along `direction` to the best head -> background step in
 * [-inward, +outward], or NaN when the profile has no clear step.
 */
const findStepOffset = (feature, point, direction, inward, outward, radius, settings) => {
  const margin = settings.stepWindowPx + 2;
  const start = -inward - margin;
  const count = inward + outward + 2 * margin + 1;
  const profile = blur1D(
    Array.from({ length: count }, (_, i) => feature.at(point.x + direction.x * (start + i), point.y + direction.y * (start + i)))
  );

  // Central difference over 3 samples; only rising steps (towards the background) count.
  const gradient = profile.map((_, i) => (i > 0 && i < count - 1 ? (profile[i + 1] - profile[i - 1]) / 2 : 0));
  const first = margin;
  const last = count - 1 - margin;
  const threshold = Math.max(
    settings.absoluteMinGradient,
    settings.relativeGradientFactor * median(gradient.slice(first, last + 1).map(Math.abs))
  );
  const minStep = settings.stepContrastFraction * feature.contrast;
  const windowMean = (from, to) => {
    let sum = 0;
    for (let i = from; i <= to; i++) sum += profile[i];
    return sum / (to - from + 1);
  };

  let bestIndex = -1;
  let bestScore = 0;
  for (let i = first; i <= last; i++) {
    const g = gradient[i];
    if (g < threshold || g < gradient[i - 1] || g < gradient[i + 1]) continue;
    const step =
      windowMean(i + 1, i + settings.stepWindowPx) - windowMean(i - settings.stepWindowPx, i - 1);
    if (step < minStep) continue;
    const score = g * (1 - (settings.distanceWeight * Math.abs(start + i)) / radius);
    if (score > bestScore) {
      bestScore = score;
      bestIndex = i;
    }
  }
  if (bestIndex < 0) return NaN;

  // Parabolic sub-pixel peak of the gradient around the best sample.
  const left = gradient[bestIndex - 1];
  const centre = gradient[bestIndex];
  const right = gradient[bestIndex + 1];
  const curvature = left - 2 * centre + right;
  const subPixel = curvature < 0 ? clamp((0.5 * (left - right)) / curvature, -0.5, 0.5) : 0;
  return clamp(start + bestIndex + subPixel, -inward, outward);
};

/** Replaces NaN shifts by linear interpolation between the nearest known neighbours (circular). */
const fillUnknown = (shifts) => {
  const known = shifts.map((shift, i) => (Number.isNaN(shift) ? -1 : i)).filter((i) => i >= 0);
  if (known.length === 0) return null;
  const n = shifts.length;
  return shifts.map((shift, i) => {
    if (!Number.isNaN(shift)) return shift;
    const nextPos = known.findIndex((k) => k > i);
    const next = nextPos >= 0 ? known[nextPos] : known[0] + n;
    const previous = nextPos > 0 ? known[nextPos - 1] : known[known.length - 1] - (nextPos === 0 ? n : 0);
    const t = (i - previous) / (next - previous);
    return shifts[((previous % n) + n) % n] * (1 - t) + shifts[next % n] * t;
  });
};

const circularMedian = (shifts, window) => {
  const half = Math.floor(window / 2);
  const n = shifts.length;
  return shifts.map((_, i) => {
    const around = [];
    for (let k = -half; k <= half; k++) around.push(shifts[(((i + k) % n) + n) % n]);
    return median(around);
  });
};

const smoothShifts = (shifts) =>
  shifts.map((shift, i) => {
    const n = shifts.length;
    return 0.5 * shift + 0.25 * (shifts[(i - 1 + n) % n] + shifts[(i + 1) % n]);
  });

/** Robust per-point shifts: fill unknowns, median filter, reject outliers, light smoothing. */
const regularizeShifts = (rawShifts, radius, settings) => {
  const knownCount = rawShifts.filter((shift) => !Number.isNaN(shift)).length;
  if (knownCount < Math.max(3, settings.minKnownFraction * rawShifts.length)) return null;
  const filled = fillUnknown(rawShifts);
  const reference = circularMedian(filled, settings.medianWindow);
  const tolerance = settings.outlierFactor * radius;
  const cleaned = filled.map((shift, i) => (Math.abs(shift - reference[i]) > tolerance ? reference[i] : shift));
  return smoothShifts(cleaned);
};

/** One refinement pass: radial search from the centroid, then regularized radial moves. */
const refinePass = (image, points, radius, settings) => {
  const centroid = calculateContourCentroid(points);
  const feature = centroid && buildFeature(image, points, centroid, settings);
  if (!feature) return { points, feature: null };

  const directions = points.map((point) => normalize({ x: point.x - centroid.x, y: point.y - centroid.y }));
  const rawShifts = points.map((point, i) => {
    if (!directions[i]) return NaN;
    const pointRadius = Math.hypot(point.x - centroid.x, point.y - centroid.y);
    const inward = Math.min(radius, Math.floor(MAX_INWARD_FRACTION * pointRadius));
    return findStepOffset(feature, point, directions[i], inward, radius, radius, settings);
  });
  const shifts = regularizeShifts(rawShifts, radius, settings);
  if (!shifts) return { points, feature };

  const moved = points.map((point, i) =>
    directions[i] ? { x: point.x + directions[i].x * shifts[i], y: point.y + directions[i].y * shifts[i] } : point
  );
  return { points: moved, feature };
};

/** Closest point of the closed polygon to `target`, with its segment index and parameter. */
const closestOnPolygon = (points, target) => {
  let best = { point: points[0], segment: 0, t: 0, distance: Infinity };
  points.forEach((a, i) => {
    const b = points[(i + 1) % points.length];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const lengthSq = dx * dx + dy * dy;
    const t = lengthSq > 0 ? clamp(((target.x - a.x) * dx + (target.y - a.y) * dy) / lengthSq, 0, 1) : 0;
    const point = { x: a.x + dx * t, y: a.y + dy * t };
    const distance = Math.hypot(point.x - target.x, point.y - target.y);
    if (distance < best.distance) best = { point, segment: i, t, distance };
  });
  return best;
};

/** Resamples the closed polygon to `count` points evenly spaced by arc length, starting at the point closest to `anchor`. */
const resampleClosed = (points, count, anchor) => {
  const n = points.length;
  const { point: startPoint, segment } = closestOnPolygon(points, anchor);
  const path = [startPoint];
  for (let k = 1; k <= n; k++) path.push(points[(segment + k) % n]);
  path.push(startPoint);

  const cumulative = [0];
  for (let i = 1; i < path.length; i++) {
    cumulative.push(cumulative[i - 1] + Math.hypot(path[i].x - path[i - 1].x, path[i].y - path[i - 1].y));
  }
  const total = cumulative[cumulative.length - 1];
  if (total <= 0) return points;

  const result = [];
  let j = 1;
  for (let k = 0; k < count; k++) {
    const target = (total * k) / count;
    while (j < path.length - 1 && cumulative[j] < target) j++;
    const span = cumulative[j] - cumulative[j - 1];
    const t = span > 0 ? (target - cumulative[j - 1]) / span : 0;
    result.push({ x: path[j - 1].x + (path[j].x - path[j - 1].x) * t, y: path[j - 1].y + (path[j].y - path[j - 1].y) * t });
  }
  return result;
};

const copyGeometry = (contour, landmarks) => ({
  contour: (contour || []).map((point) => ({ x: point.x, y: point.y })),
  // Landmarks (nose, ears) are passed through untouched: they are not on the contour.
  landmarks: landmarks
    ? Object.keys(landmarks).reduce((acc, key) => {
        acc[key] = landmarks[key] && typeof landmarks[key] === "object" ? { ...landmarks[key] } : landmarks[key];
        return acc;
      }, {})
    : landmarks,
});

/**
 * @param {{ pixels: Uint8ClampedArray, width: number, height: number,
 *   contour: Array<{x:number,y:number}>, landmarks: { nose:{x,y}, earLeft:{x,y}, earRight:{x,y} }, options?: object }} input
 *   Clean RGBA pixels (ImageData.data) and the normalized AI geometry (contour clockwise, implicitly closed).
 * @returns {{ contour: Array<{x:number,y:number}>, landmarks: { nose:{x,y}, earLeft:{x,y}, earRight:{x,y} },
 *   stats: { movedPoints: number, meanShiftPx: number, maxShiftPx: number, radiusPx: number, passes: number,
 *     contrast: number, featureMode: 'color'|'luminance' } }}
 *   Normalized refined geometry clamped to [0, 1]; shifts are in pixels of the given buffer (distance of each
 *   point before the final resample), meanShiftPx averages over all contour points and radiusPx is the first-pass R.
 */
export const refineContour = ({ pixels, width, height, contour, landmarks, options }) => {
  const settings = { ...DEFAULT_REFINEMENT_OPTIONS, ...(options || {}) };
  const radius = clamp(Math.round(settings.radiusFactor * Math.max(width, height)), settings.minRadiusPx, settings.maxRadiusPx);
  const emptyStats = { movedPoints: 0, meanShiftPx: 0, maxShiftPx: 0, radiusPx: radius, passes: 0, contrast: 0, featureMode: "luminance" };

  const original = Array.isArray(contour) && contour.every(isPoint) ? contour.map((p) => ({ x: p.x * width, y: p.y * height })) : [];
  if (original.length < 3 || !calculateContourCentroid(original) || !pixels || width < 3 || height < 3) {
    return { ...copyGeometry(contour, landmarks), stats: emptyStats };
  }

  const image = { planes: toBlurredRgb(pixels, width, height), width, height };
  let points = original;
  let firstFeature = null;
  let passes = 0;
  for (let pass = 0; pass < settings.passes; pass++) {
    const passRadius = Math.max(4, Math.round(radius * settings.passRadiusFactor ** pass));
    const result = refinePass(image, points, passRadius, settings);
    points = result.points;
    firstFeature = firstFeature || result.feature;
    if (result.feature) passes++;
  }

  const magnitudes = points.map((point, i) => Math.hypot(point.x - original[i].x, point.y - original[i].y));
  const movedPoints = magnitudes.filter((value) => value > MOVED_EPSILON_PX).length;
  const stats = {
    movedPoints,
    meanShiftPx: magnitudes.reduce((sum, value) => sum + value, 0) / magnitudes.length,
    maxShiftPx: Math.max(...magnitudes),
    radiusPx: radius,
    passes,
    contrast: firstFeature ? Math.round(firstFeature.contrast) : 0,
    featureMode: firstFeature ? firstFeature.mode : "luminance",
  };
  if (movedPoints === 0) return { ...copyGeometry(contour, landmarks), stats };

  const refinedPx = resampleClosed(points, original.length, original[0]);
  const toNorm = (point) => ({ x: clamp01(point.x / width), y: clamp01(point.y / height) });

  return { contour: refinedPx.map(toNorm), landmarks: copyGeometry([], landmarks).landmarks, stats };
};
